"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import {
  getFCMToken,
  showForegroundNotification,
  subscribeToForegroundMessages,
} from "@/utils/firebase";
import { getCookie } from "@/utils/cookie";
import { useAuthStore } from "@/store/useAuthStore";

const getNotificationRedirectUrl = (data = {}) => {
  if (data?.url) return data.url;

  const notificationType = data?.type || "";
  const conversationId = data?.conversationId || data?.conversation_id || null;

  const messageNotificationTypes = [
    "NEW_MESSAGE",
    "MESSAGE",
    "MESSAGE_RECEIVED",
    "CHAT_MESSAGE",
  ];

  if (
    messageNotificationTypes.includes(notificationType) ||
    notificationType.toLowerCase().includes("message")
  ) {
    return conversationId
      ? `/messages?conversationId=${encodeURIComponent(conversationId)}`
      : "/messages";
  }

  const matchNotificationTypes = [
    "MATCH_APPROVED",
    "MATCH_REJECTED",
    "NEW_MATCH",
    "PROFILE_LIKED",
  ];

  const getMatchProfileUserId = () =>
    data?.counterpartUserId ||
    data?.counterpart_user_id ||
    data?.matchedUserId ||
    data?.matched_user_id ||
    data?.profileUserId ||
    data?.profile_user_id ||
    data?.otherUserId ||
    data?.other_user_id ||
    data?.likedByUserId ||
    data?.liked_by_user_id ||
    data?.likerUserId ||
    data?.liker_user_id ||
    null;

  if (matchNotificationTypes.includes(notificationType)) {
    const matchProfileUserId = getMatchProfileUserId();
    return matchProfileUserId
      ? `/profile-detail?userId=${encodeURIComponent(matchProfileUserId)}`
      : "/matches";
  }

  const circleNotificationTypes = [
    "CIRCLE_INVITE_RECEIVED",
    "CIRCLE_INVITE_ACCEPTED",
    "CIRCLE_INVITE_DECLINED",
    "CIRCLE_MEMBER_JOINED",
    "CIRCLE_VOTE_SUBMITTED",
    "CIRCLE_OVERRIDE",
  ];

  if (circleNotificationTypes.includes(notificationType)) {
    return "/circle";
  }

  return "/notifications";
};

const PushNotificationManager = () => {
  const router = useRouter();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

  useEffect(() => {
    if (!isLoggedIn && !getCookie("access_token")) {
      return undefined;
    }

    getFCMToken();

    const unsubscribe = subscribeToForegroundMessages((payload) => {
      const title = payload?.notification?.title || "New Notification";
      const body = payload?.notification?.body || "";
      const targetUrl = getNotificationRedirectUrl(payload?.data);

      toast(title, {
        description: body,
        action: {
          label: "View",
          onClick: () => {
            if (targetUrl) {
              router.push(targetUrl);
            }
          },
        },
      });

      showForegroundNotification(payload);
    });

    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === "FCM_NOTIFICATION_CLICK" && event.data?.url) {
        router.push(event.data.url);
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    }

    return () => {
      unsubscribe();
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage,
        );
      }
    };
  }, [isLoggedIn, router]);

  return null;
};

export default PushNotificationManager;
